import { Injectable } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { ModalController } from '@ionic/angular/standalone';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { isAfter, isBefore, startOfToday } from 'date-fns';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { KanbanItemViewDialogComponent } from '../kanban/view/item/item-view.dialog';
import { Room } from '../models/room.model';
import { SiteArea } from '../models/site-area.model';
import { Site } from '../models/site.model';
import { RoomTicket, Ticket, TicketStatus } from '../models/ticket.model';
import { User } from '../models/user.model';
import { findParent, getLeaf, parseAreaTicketGuid, setSiteArea } from '../utility/kanban';
import { deepClone } from '../utility/object';
import { adminActionRoles, Roles } from '../utility/role';
import { getPreview } from '../utility/ticket';
import { isTimestamp } from '../utility/time';
import { SiteAreasService } from './site-areas.service';
import { TicketsService } from './tickets.service';
import { UsersService } from './users.service';

@Injectable({
  providedIn: 'root',
})
export class AreasService {

  constructor(
    private modalCtrl: ModalController,
    private siteAreasService: SiteAreasService,
    private ticketsService: TicketsService,
    private usersService: UsersService,
  ) { }

  private getRoomFromArea(area: SiteArea, roomId: string, childAreaGuid: string) {
    let room: Room = null;
    let retArea: SiteArea = null;
    if ((childAreaGuid == null || area.id === childAreaGuid) && area.rooms?.length > 0) {
      room = area.rooms.find((r) => r.id === roomId);
      retArea = deepClone(area);
    } else if (area.children?.length > 0) {
      area.children.map((c) => {
        if (room === null) {
          const childRoom = this.getRoomFromArea(c, roomId, childAreaGuid).room;
          if (childRoom !== null) {
            room = childRoom;
            retArea = deepClone(c);
          }
        }
      });
    }
    return { room, area: retArea };
  }

  private getAreaFromArea(area: SiteArea, childAreaGuid: string) {
    let retArea: SiteArea = null;
    if (childAreaGuid == null || area.id === childAreaGuid) {
      retArea = deepClone(area);
    } else if (area.children?.length > 0) {
      area.children.map((c) => {
        if (retArea === null) {
          const childArea = this.getAreaFromArea(c, childAreaGuid);
          if (childArea !== null) {
            retArea = deepClone(c);
          }
        }
      });
    }
    return retArea;
  }

  private isInFuture(ts: Timestamp | Date) {
    let date: Date;
    if (isTimestamp(ts)) {
      date = ts.toDate();
    } else {
      date = ts;
    }
    return isBefore(startOfToday(), date);
  }

  currentUserHasAccess() {
    return this.usersService.getCurrentUser().pipe(
      filter((user) => user != null),
      switchMap((user) => this.userUserHasAccess(user)),
      shareReplay(1),
    );
  }

  userUserHasAccess(user: User, client?: string) {
    if (user.role !== Roles.resident) {
      return of(true);
    }
    return this.siteAreasService.getList({ access: user.guid, client }).pipe(
      map((areas) => {
        if (user.role !== Roles.resident) {
          return true;
        }
        const accesses = areas.map((a) => a.users.filter((u) => u.guid === user.guid)).flat();
        const hasAccess = accesses.some((it) => it.end == null || this.isInFuture(it.end));
        return hasAccess;
      }),
      shareReplay(1),
    );
  }

  getMyAreas(site?: string) {
    return this.usersService.getCurrentUser().pipe(
      filter((user) => user != null),
      switchMap((user) => {
        if ([Roles.resident].includes(user.role)) {
          return combineLatest([this.siteAreasService.getList({ site, access: user.guid }), this.siteAreasService.getList({ site, common: true })]).pipe(
            map(([areas, commonAreas]) => [...areas, ...commonAreas?.sort(fieldSorter(['name']))]),
            shareReplay(1),
          );
        } else if (adminActionRoles.includes(user.role)) {
          return this.siteAreasService.getList({ site, client: user.clients[0] }).pipe(
            map((areas) => [...areas?.sort(fieldSorter(['name']))]),
            shareReplay(1),
          );
        } else if ([Roles.contractorAdmin, Roles.worker].includes(user.role)) {
          return this.siteAreasService.getList({ site }).pipe(
            map((areas) => [...areas?.sort(fieldSorter(['name']))]),
            shareReplay(1),
          );
        }
      }),
      shareReplay(1),
    );
  }

  getMyResidences(site?: string) {
    const user = this.usersService.currentUserS();
    if ([Roles.resident].includes(user.role)) {
      return this.siteAreasService.getList({ site, access: user.guid }).pipe(
        shareReplay(1),
      );
    }
  }

  getAccessAreas(site?: string) {
    const user = this.usersService.currentUserS();
    return this.siteAreasService.getList({ site, access: user.guid }).pipe(
      shareReplay(1),
    );
  }

  getPreview(rt: RoomTicket) {
    return getPreview(rt);
  }

  getArea(guid: string, id: string) {
    return this.siteAreasService.get(guid).pipe(
      map((root) => this.getAreaFromArea(root, id)),
    );
  }

  getAreas(site$: Observable<Site>): Observable<SiteArea[]> {
    return site$.pipe(
      switchMap((site) => this.siteAreasService.getList({ site: site.guid })),
      shareReplay(1),
    );
  }

  getSiteAreas(site: string): Observable<SiteArea[]> {
    return this.siteAreasService.getList({ site }).pipe(
      shareReplay(1),
    );
  }

  getAreaTickets(_sites$: Observable<{ guid?: string }[]>, _contractor?: string): Observable<RoomTicket[]> {
    return of([]);
  }

  getRoomsFromAreas(areas: SiteArea[]) {
    return areas.map((area) => {
      const leafs = getLeaf(area);
      return leafs.map((it) => it.rooms.map((room) => setSiteArea(room, area, it))).flat();
    }).flat();
  }

  getAreaRooms(site$: Observable<Site>): Observable<Room[]> {
    return site$.pipe(
      switchMap((site) => this.siteAreasService.getList({ site: site.guid })),
      map((areas) => this.getRoomsFromAreas(areas)),
    );
  }

  getRoom(ticketGuid: string, root: SiteArea): Room {
    const parsed = parseAreaTicketGuid(ticketGuid);
    const parent = findParent(root, parsed.childAreaGuid);
    const { room, area } = this.getRoomFromArea(root, parsed.roomId, parsed.childAreaGuid);
    if (room == null) {
      return {
        id: parsed.roomId ?? null,
        siteArea: {
          root: root.guid,
          id: parsed.childAreaGuid ?? parent.id,
          name: root.name !== area?.name ? `${root.name} / ${area?.name ?? ''}` : `${parent.name}`,
        },
        type: 'common',
      };
    }
    room.siteArea = {
      root: root.guid,
      id: parent.id,
      name: root.name !== area?.name ? `${root.name} / ${area?.name ?? ''}` : `${parent.name}`,
    };
    return room;
  }

  getRoomId(room: string | Room) {
    if (typeof room === 'string') {
      return room;
    }
    return room.id;
  }

  findRoom(room: string | Room, area: SiteArea): Room {
    const roomName = this.getRoomId(room);
    if (area.children?.length > 0) {
      const children = area.children.map((child) => this.findRoom(roomName, child)).filter((it) => it != null);
      if (children.length > 0) {
        return children[0];
      }
    } else if (area.rooms) {
      return area.rooms.find((it) => it.id.toLowerCase() === roomName.toLowerCase());
    }
    return null;
  }

  findArea(areaName: string, area: SiteArea): SiteArea {
    if (area.name.toLowerCase() === areaName.toLowerCase()) {
      return area;
    } else if (area.children?.length > 0) {
      const children = area.children.map((child) => this.findArea(areaName, child)).filter((it) => it != null);
      if (children.length > 0) {
        return children[0];
      }
    }
    return null;
  }

  getUserTickets(tickets: Ticket[], userGuid: string, status?: TicketStatus, startDate?: Date, endDate?: Date) {
    const roomTickets = tickets.map((it) => it.tickets).flat();
    const usersTickets = roomTickets
      .filter((it) => it.users?.includes(userGuid))
      .filter((it) => status ? it.status === status : true)
      .map((it) => {
        const done = it.history.find((itt) => itt.status === TicketStatus.done);
        it.date = done?.timestamp;
        return it;
      })
      .filter((it) => startDate && endDate ? !isAfter(it.date, endDate) && !isBefore(it.date, startDate) : true);
    return usersTickets;
  }

  openRoomTicket(roomTicketGuid: string) {
    combineLatest([this.ticketsService.get(roomTicketGuid)]).pipe(
      take(1),
    ).subscribe(([ticket]) => {
      this.openModal(ticket);
    });
  }

  private openModal(roomTicket: RoomTicket) {
    this.modalCtrl.create({
      component: KanbanItemViewDialogComponent,
      componentProps: {
        ticket: roomTicket,
      },
    }).then((m) => {
      m.present();
    });
  }
}
